import { Pipeline } from "@/Contracts";
import { OpportunityShareStatus, OpportunityView } from "@/Contracts/Opportunity";
import { SalesActivityAutomationMetadataFilterOperator } from "@/Contracts/Others/SalesActivity";
import { CustomViewFilterCriteria } from "@/Contracts/UI";
import { ZomentumEntities } from "@/Contracts/common";
import { isVendorApp } from "..";
import { EIcons } from "@atoms/ZIcon/interface";
import { EColors } from "@atoms/ZColors/interface";

export const getOpportunityCardColorCodeClass = (
	opportunityShareStatus: OpportunityShareStatus
): string => {
	switch (opportunityShareStatus) {
		case OpportunityShareStatus.WAITING:
			return "waiting-opportunity-color-code";
		case OpportunityShareStatus.ACCEPTED:
			return "accepted-opportunity-color-code";
		case OpportunityShareStatus.REJECTED:
			return "rejected-opportunity-color-code";
		case OpportunityShareStatus.SHARED_BY_PARTNER:
		case OpportunityShareStatus.SHARED_BY_VENDOR:
			return "vendor-opportunity-color-code";
		default:
			return "self-opportunity-color-code";
	}
};

export const getIconPropsBasedOnShareStatus = (
	status: OpportunityShareStatus
) => {
	switch (status) {
		case OpportunityShareStatus.WAITING:
			return {
				name: EIcons.CLOCK,
				color: EColors.CYAN
			};
		case OpportunityShareStatus.ACCEPTED:
			return {
				name: EIcons.CHECKCIRCLE,
				color: EColors.GREEN
			};
		case OpportunityShareStatus.REJECTED:
			return {
				name: EIcons.BLOCK,
				color: EColors.RED
			};
		default:
			return {
				name: EIcons.CHECKCIRCLE,
				color: EColors.GEEKBLUE
			};
	}
};

export const getDisplayNameBasedOnShareStatus = (
	status: OpportunityShareStatus
) => {
	const isPartnerAlign = isVendorApp();
	switch (status) {
		case OpportunityShareStatus.SHARED_BY_PARTNER:
			return "Shared By Partner";
		case OpportunityShareStatus.SHARED_BY_VENDOR:
			return "Shared By Vendor";
		default:
			return `Shared With ${isPartnerAlign ? "Partner" : "Vendor"}`;
	}
};

export const getAlertClassNameBasedOnShareStatus = (
	status: OpportunityShareStatus
) => {
	switch (status) {
		case OpportunityShareStatus.WAITING:
			return "bg-cyan-1 border-cyan-3";
		case OpportunityShareStatus.ACCEPTED:
			return "bg-green-1 border-green-3";
		case OpportunityShareStatus.REJECTED:
			return "bg-red-1 border-red-3";
		default:
			return "bg-geekBlue-1 border-geekBlue-3";
	}
};

export const getTooltipTextBasedOnShareStatus = (
	status: OpportunityShareStatus,
	sharedAt?: string
) => {
	switch (status) {
		case OpportunityShareStatus.WAITING:
			return `Waiting to Accept${
				!!sharedAt?.length ? `. Shared on ${sharedAt}` : ""
			}`;
		case OpportunityShareStatus.ACCEPTED:
		case OpportunityShareStatus.SHARED_BY_PARTNER:
		case OpportunityShareStatus.SHARED_BY_VENDOR:
			return "Accepted";
		case OpportunityShareStatus.REJECTED:
			return "Rejected";
		default:
			return "";
	}
};

export const getTooltipTextBasedOnShareStatusOppDetails = (
	status: OpportunityShareStatus,
	sharedAt?: string
) => {
	const isPartnerAlign = isVendorApp();
	switch (status) {
		case OpportunityShareStatus.WAITING:
			return `${
				!!sharedAt?.length ? `Shared on: ${sharedAt}. ` : ""
			}Waiting to Accept. Updates made won’t be visible to ${
				isPartnerAlign ? "partner" : "vendor"
			} until they accept the opportunity.`;
		case OpportunityShareStatus.ACCEPTED:
		case OpportunityShareStatus.SHARED_BY_PARTNER:
		case OpportunityShareStatus.SHARED_BY_VENDOR:
			return "Accepted";
		case OpportunityShareStatus.REJECTED:
			return `Rejected. Please reassign opportunity to different ${
				isPartnerAlign ? "partner" : "vendor"
			}.`;
		default:
			return "";
	}
};

export const getUpdatedFiltersWithPipelineFilters = (
	pipelineId?: string,
	oldFilterCriterion: CustomViewFilterCriteria[] = []
) => {
	const pipelineFilter: CustomViewFilterCriteria = {
		field_display_name: "",
		filter_entity_type: ZomentumEntities.Opportunity,
		field_name: "opportunity_pipeline_id",
		filter_operator: {
			id_value: SalesActivityAutomationMetadataFilterOperator.Is,
			display_value: "Is",
			value_type: null
		},
		values: [
			{
				id_value: pipelineId ?? "",
				display_value: "",
				value_type: null
			}
		],
		linked_entity_type: null
	};

	const pipelineFilterIndex = oldFilterCriterion?.findIndex(
		filter => filter.field_name === "opportunity_pipeline_id"
	);

	let updatedFilters = oldFilterCriterion;

	if (pipelineFilterIndex > -1) {
		const clonedFilters = [...updatedFilters];
		clonedFilters.splice(pipelineFilterIndex, 0, pipelineFilter);
		updatedFilters = [...clonedFilters];
	} else {
		updatedFilters = [...updatedFilters, pipelineFilter];
	}
	return updatedFilters;
};

export const computeExportRecordCount = (
	activeView: OpportunityView,
	kanbanCount: Record<string, number>,
	filteredCountMap: Record<string, number> | null
): number => {
	if (activeView === OpportunityView.Kanban) {
		return Object.values(kanbanCount).reduce((sum, n) => sum + n, 0);
	}
	return filteredCountMap?.all ?? 0;
};

export const dummyAllPipeline: Pipeline = {
	id: "00000000000000000000000000000000",
	created: "2021-03-30T13:20:12.459Z",
	updated: "2023-11-15T06:07:55.696Z",
	name: "All Pipelines",
	company_id: "00000000000000000000000000000000",
	opportunity_stages: [],
	user_id: "00000000000000000000000000000000",
	is_default: false,
	connected_sync_sources: []
};

export const isAllPipelinesFilter = (
	pipelineId: string | null | undefined
): boolean => pipelineId === dummyAllPipeline.id;
